"use client";

import { useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { ArrowUpRight } from "lucide-react";
import { useForm } from "@formeus/react";
import { toast } from "sonner";
import { SectionHeader } from "./ui/section-header";

export function ContactForm() {
  const loadedAtRef = useRef<number>(Date.now());
  const [honeypot, setHoneypot] = useState("");

  const { values, update, validations, submit, isSubmitting } = useForm({
    initial: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
    validators: {
      name: ({ name }) => {
        const trimmed = name.trim();
        if (trimmed.length === 0) {
          return new Error("Ime i prezime je obavezno");
        }
        const parts = trimmed.split(/\s+/).filter((p) => p.length >= 2);
        if (parts.length < 2) {
          return new Error("Unesite ime i prezime");
        }
        if (!/^[\p{L}\p{M}'\-.\s]+$/u.test(trimmed)) {
          return new Error("Ime sadrži nedopuštene znakove");
        }
        return undefined;
      },
      email: ({ email }) => {
        if (email.trim().length === 0) {
          return new Error("Email adresa je obavezna");
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          return new Error("Unesite valjanu email adresu");
        }
        return undefined;
      },
      phone: ({ phone }) => {
        const trimmed = phone.trim();
        if (trimmed.length === 0) return undefined;
        if (!/^[\d\s+()\-./]{6,30}$/.test(trimmed)) {
          return new Error("Unesite valjan broj telefona");
        }
        return undefined;
      },
      message: ({ message }) => {
        const trimmed = message.trim();
        if (trimmed.length === 0) {
          return new Error("Poruka je obavezna");
        }
        if (trimmed.length < 10) {
          return new Error("Poruka mora imati barem 10 znakova");
        }
        if (trimmed.split(/\s+/).filter(Boolean).length < 3) {
          return new Error("Napišite nešto više o vašem upitu");
        }
        return undefined;
      },
    },
    onSubmitForm: async ({ name, email, phone, message }) => {
      const sendPromise = fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          message,
          website: honeypot,
          loadedAt: loadedAtRef.current,
        }),
      }).then(async (res) => {
        if (!res.ok) {
          let errText = "Slanje nije uspjelo. Pokušajte ponovno.";
          try {
            const data = await res.json();
            if (data?.error) errText = String(data.error);
          } catch {}
          throw new Error(errText);
        }
        return res.json();
      });

      toast.promise(sendPromise, {
        loading: "Slanje poruke...",
        success: "Vaša poruka je uspješno poslana. Hvala vam na povjerenju!",
        error: (err) =>
          err instanceof Error
            ? err.message
            : "Došlo je do greške pri slanju. Pokušajte ponovno.",
      });

      try {
        await sendPromise;
        update("name", "");
        update("email", "");
        update("phone", "");
        update("message", "");
      } catch {}
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit();
  };

  const fieldClass =
    "h-12 px-0 border-0 border-b border-brand-300/60 bg-transparent shadow-none focus-visible:border-brand-800 focus-visible:ring-0 text-base placeholder:text-foreground/35";

  const labelClass =
    "uppercase text-[10px] tracking-[0.2em] text-brand-800 leading-none";

  return (
    <div className="flex flex-col w-full gap-10">
      <SectionHeader
        eyebrow="Obrazac"
        title="Pošaljite nam"
        accent="poruku"
        description="Ispunite obrazac u nastavku — javit ćemo Vam se u najkraćem roku."
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-7">
        <div
          aria-hidden="true"
          className="absolute h-0 w-0 overflow-hidden opacity-0 pointer-events-none -left-[9999px]"
        >
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-7">
          <div className="flex flex-col gap-2">
            <Label htmlFor="name" className={labelClass}>
              Ime i prezime
            </Label>
            <Input
              type="text"
              id="name"
              name="name"
              placeholder="Ivan Horvat"
              value={values.name}
              onChange={(e) => update("name", e.target.value)}
              className={fieldClass}
            />
            {validations.name.error && (
              <p className="text-[13px] text-destructive mt-1">
                {validations.name.error.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="email" className={labelClass}>
              Email adresa
            </Label>
            <Input
              type="email"
              id="email"
              name="email"
              placeholder="hello@dentivo.hr"
              value={values.email}
              onChange={(e) => update("email", e.target.value)}
              className={fieldClass}
            />
            {validations.email.error && (
              <p className="text-[13px] text-destructive mt-1">
                {validations.email.error.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="phone" className={labelClass}>
            Telefon{" "}
            <span className="font-serif italic normal-case tracking-normal text-foreground/40 ml-1.5 text-[11px]">
              opcionalno
            </span>
          </Label>
          <Input
            type="tel"
            id="phone"
            name="phone"
            placeholder="051 276 745"
            value={values.phone}
            onChange={(e) => update("phone", e.target.value)}
            className={fieldClass}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="message" className={labelClass}>
            Poruka
          </Label>
          <Textarea
            id="message"
            name="message"
            placeholder="Pozdrav! Želim rezervirati termin..."
            className="resize-none min-h-32 px-0 border-0 border-b border-brand-300/60 bg-transparent shadow-none focus-visible:border-brand-800 focus-visible:ring-0 text-base placeholder:text-foreground/35"
            value={values.message}
            onChange={(e) => update("message", e.target.value)}
          />
          {validations.message.error && (
            <p className="text-[13px] text-destructive mt-1">
              {validations.message.error.message}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 pt-4">
          <p className="text-[12px] text-foreground/45 leading-[1.5] max-w-[280px]">
            Slanjem obrasca pristajete na obradu osobnih podataka.
          </p>
          <button
            type="submit"
            disabled={isSubmitting}
            className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-foreground/85 hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {isSubmitting ? "Šalje se..." : "Pošaljite"}
            <span className="relative grid place-items-center size-11 border border-brand-800/30 overflow-hidden transition-colors duration-500 group-hover:border-brand-800">
              <span
                aria-hidden
                className="absolute inset-0 origin-bottom scale-y-0 bg-brand-800 transition-transform duration-500 ease-[cubic-bezier(0.6,-0.05,0.01,0.99)] group-hover:scale-y-100"
              />
              <ArrowUpRight className="relative size-4 text-brand-800 transition-colors duration-500 group-hover:text-background" />
            </span>
          </button>
        </div>
      </form>
    </div>
  );
}
